import { useState, useMemo } from 'react';
import { useDebounce } from '@/shared/hooks';
import { type Researcher } from '@aphp-ema/common/researchers';
import { type SearchParams } from '@/features/researchers/types';

/**
 * Custom hook for handling user search functionality
 * @returns Search state and handlers
 */
export const useUserSearch = (rows: readonly Researcher[] = []) => {
  // Initialize search parameters
  const [searchParams, setSearchParams] = useState<SearchParams>({
    firstName: '',
    lastName: '',
    email: '',
  });

  // Debounce search parameters to prevent excessive filtering
  const debouncedSearchParams = useDebounce(searchParams, 300);

  // Check if any search parameters are active
  const hasActiveSearch = useMemo(
    () => Object.values(searchParams).some((value) => value !== ''),
    [searchParams],
  );

  // Filter rows based on search parameters
  const filteredRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    // If no search parameters are active, return all rows
    if (!hasActiveSearch) return rows;

    return rows.filter((row: Researcher) => {
      // Check firstName
      if (
        debouncedSearchParams.firstName &&
        (!row.firstName ||
          !row.firstName
            .toLowerCase()
            .includes(debouncedSearchParams.firstName.toLowerCase()))
      ) {
        return false;
      }

      // Check lastName
      if (
        debouncedSearchParams.lastName &&
        (!row.lastName ||
          !row.lastName
            .toLowerCase()
            .includes(debouncedSearchParams.lastName.toLowerCase()))
      ) {
        return false;
      }

      // Check email
      if (
        debouncedSearchParams.email &&
        (!row.email ||
          !row.email
            .toLowerCase()
            .includes(debouncedSearchParams.email.toLowerCase()))
      ) {
        return false;
      }

      // If all checks pass, include the row
      return true;
    });
  }, [rows, debouncedSearchParams, hasActiveSearch]);

  // Handle input changes
  const handleSearchChange = (field: string, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Clear all search fields
  const handleClearSearch = () => {
    setSearchParams({
      firstName: '',
      lastName: '',
      email: '',
    });
  };

  return {
    searchParams,
    hasActiveSearch,
    filteredRows,
    handleSearchChange,
    handleClearSearch,
  };
};
